import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import Modal, { field, lbl } from "./Modal";
import ImageUploader from "./ImageUploader";
import { Pencil, Trash2, GripVertical } from "lucide-react";

const EMPTY = { title: "", strain_ref: "", photos: [], status: "READY", description: "", price: "", sort_order: 0 };

export default function DropsTab() {
  const [drops, setDrops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = () => client.get("/drops").then(({ data }) => setDrops(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...EMPTY, ...d, price: d.price || "" }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    setSaving(true);
    try {
      const payload = { ...form, price: form.price || null };
      if (editing) await client.put(`/admin/drops/${editing.id}`, payload);
      else await client.post("/admin/drops", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete drop ${d.title}?`)) return;
    await client.delete(`/admin/drops/${d.id}`);
    load();
  };

  const setStatus = async (d, status) => {
    await client.patch(`/admin/drops/${d.id}/status`, null, { params: { status } });
    load();
  };

  // native drag reorder
  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...drops];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDrops(next);
    setDragIdx(null);
    await client.post("/admin/drops/reorder", { order: next.map((d) => d.id) });
    toast.success("Order saved");
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl">DROPS <span className="text-yaza-accent">{drops.length}</span></h2>
        <button onClick={openNew} data-testid="new-drop-btn" className="rounded-none bg-yaza-accent px-5 py-2 text-xs font-bold uppercase tracking-widest text-black hover:bg-yaza-accentHover">+ New Drop</button>
      </div>
      <p className="mb-3 text-xs text-neutral-500">Drag rows to reorder. Change status inline.</p>

      <div className="overflow-hidden rounded-lg border border-yaza-border">
        {drops.map((d, i) => (
          <div
            key={d.id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            data-testid={`admin-drop-${i}`}
            className="flex items-center gap-3 border-b border-yaza-border px-3 py-3 last:border-0"
          >
            <GripVertical size={16} className="cursor-grab text-neutral-600" />
            <img src={d.photos?.[0]} alt="" className="h-12 w-12 rounded object-cover bg-yaza-surface2" />
            <p className="flex-1 font-semibold">{d.title}</p>
            <select value={d.status} onChange={(e) => setStatus(d, e.target.value)} data-testid={`drop-status-${i}`} className="rounded border border-yaza-border bg-black px-2 py-1 text-xs">
              <option value="READY">Ready Now</option><option value="SOON">Dropping Soon</option><option value="SOLD_OUT">Sold Out</option>
            </select>
            <button onClick={() => openEdit(d)} data-testid={`edit-drop-${i}`} className="text-neutral-400 hover:text-white"><Pencil size={16} /></button>
            <button onClick={() => del(d)} data-testid={`delete-drop-${i}`} className="text-neutral-400 hover:text-yaza-accent"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Drop" : "New Drop"}>
        <div className="grid gap-4">
          <div><label className={lbl}>Title</label><input className={field} value={form.title} onChange={set("title")} data-testid="drop-title" /></div>
          <div><label className={lbl}>Strain slug ref (optional)</label><input className={field} value={form.strain_ref} onChange={set("strain_ref")} /></div>
          <div>
            <label className={lbl}>Status</label>
            <select className={field} value={form.status} onChange={set("status")} data-testid="drop-form-status">
              <option value="READY">Ready Now</option><option value="SOON">Dropping Soon</option><option value="SOLD_OUT">Sold Out</option>
            </select>
          </div>
          <div><label className={lbl}>Price (optional)</label><input className={field} value={form.price} onChange={set("price")} placeholder="leave blank to hide" /></div>
          <div><label className={lbl}>Description</label><textarea className={field} rows={3} value={form.description} onChange={set("description")} /></div>
          <div><label className={lbl}>Photos</label><ImageUploader images={form.photos} onChange={(imgs) => setForm((f) => ({ ...f, photos: imgs }))} testid="drop-images" /></div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-drop" className="mt-6 w-full rounded-none bg-yaza-accent py-3 text-sm font-bold uppercase tracking-widest text-black hover:bg-yaza-accentHover disabled:opacity-50">{saving ? "Saving…" : "Save Drop"}</button>
      </Modal>
    </div>
  );
}
